import type { ReactNode } from "react";

import { cn } from "@/engine/support/classNames";

import {
  RoomModePicker,
  type RoomMode,
  type RoomModeOption,
} from "./RoomModePicker";

export interface RoomSettingsPanelProps {
  /** Room name as it appears in the top bar. */
  name: string;
  nameLabel: string;
  onNameChange: (name: string) => void;
  mode: RoomMode;
  modeLabel: string;
  options: readonly RoomModeOption[];
  onModeChange: (mode: RoomMode) => void;
  /** The stage beside the kinds, drawn as the chosen one would look. */
  preview?: ReactNode;
  /** The application's handles on the panel's controls, for its browser tests. */
  testIds?: RoomSettingsTestIds | undefined;
  className?: string | undefined;
}

export type RoomSettingsTestIds = Partial<
  Record<"name" | RoomMode, string | undefined>
>;

/** A room's name and the kind it is, chosen as it is opened. */
export function RoomSettingsPanel({
  name,
  nameLabel,
  onNameChange,
  mode,
  modeLabel,
  options,
  onModeChange,
  preview,
  testIds = {},
  className,
}: RoomSettingsPanelProps) {
  return (
    <div
      className={cn(
        "grid min-w-0 gap-5",
        preview ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]" : null,
        className,
      )}
    >
      <div className="min-w-0 space-y-4">
        <label className="block space-y-2">
          <span className="text-sm font-medium">{nameLabel}</span>
          <input
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            data-testid={testIds.name}
            className="h-10 w-full rounded-[10px] border border-raise bg-background px-3 text-base outline-none focus:border-primary"
          />
        </label>
        <fieldset className="space-y-3">
          <legend className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {modeLabel}
          </legend>
          <RoomModePicker
            value={mode}
            options={options}
            testIds={testIds}
            onChange={onModeChange}
          />
        </fieldset>
      </div>
      {preview ? (
        <div className="h-96 rounded-2xl border border-border bg-app p-3">
          {preview}
        </div>
      ) : null}
    </div>
  );
}
